/**
 * batchProgress.js — Canonical item-counting rule engine for batches.
 *
 * SINGLE SOURCE OF TRUTH for how items on a batch are counted in the three
 * semantic dimensions the backbone cares about. Every consumer that displays
 * a progress bar, computes revenue, or projects completion MUST read from
 * here — never from raw `batch.items_completed` directly.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * The three semantics
 * ═══════════════════════════════════════════════════════════════════════
 *
 * 1. CREDITED  — progress (what a dashboard shows)
 *    = items_completed - items_rejected
 *    Counts in-flight work. Excludes items rejected outright.
 *    Use for: completion %, ETA, dashboard progress, "how close are we to done?"
 *
 * 2. BILLABLE  — accrual revenue (GAAP-compliant)
 *    = on Delivered:  items_completed - items_rejected  (tendered to client)
 *    = on Accepted:   items_accepted                    (client-confirmed count)
 *    = else:          0
 *    Use for: revenue recognition in financial reports, monthly invoice computation
 *
 * 3. ACCEPTED  — cash-conservative (only client-confirmed)
 *    = on Accepted:   items_accepted
 *    = else:          0
 *    Use for: conservative financial close, audit-defensible revenue
 *
 * ═══════════════════════════════════════════════════════════════════════
 * Invariants (enforced by tests; North Star §6)
 * ═══════════════════════════════════════════════════════════════════════
 *
 *   For every batch:
 *     credited ≥ billable ≥ accepted ≥ 0
 *     credited + rejected ≤ total_items
 *
 *   For non-(Delivered|Accepted) batches:
 *     billable === 0
 *
 *   For non-Accepted batches:
 *     accepted === 0
 *
 * ═══════════════════════════════════════════════════════════════════════
 * The 4-state lifecycle (backbone)
 * ═══════════════════════════════════════════════════════════════════════
 *
 *     In Progress  →  In Review  →  Delivered  →  Accepted (terminal)
 *
 *   Rejection loops back to In Progress with a rejection_note.
 *   Unassigned batches are In Progress with individual_id = null.
 *   See `_NORTH-STAR.md` §5.2 for the full lifecycle spec.
 */

/**
 * The 4 canonical batch states. Frozen to prevent mutation.
 * Use BATCH_STATES.IN_PROGRESS etc. instead of string literals anywhere in the app.
 */
export const BATCH_STATES = Object.freeze({
  IN_PROGRESS: 'In Progress',
  IN_REVIEW: 'In Review',
  DELIVERED: 'Delivered',
  ACCEPTED: 'Accepted',
});

// ─── Internal helpers ────────────────────────────────────────────────────

/** Safe integer coercion — returns 0 for null/undefined/NaN/non-numeric/negative. */
function toNonNegInt(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.floor(n);
}

/** Pull a non-negative integer field off an object, defaulting to 0. */
function intField(batch, field) {
  if (!batch) return 0;
  return toNonNegInt(batch[field]);
}

// ─── Per-batch functions ─────────────────────────────────────────────────

/**
 * Credited items — progress-facing count.
 * @param {Object} batch — an annotation_batch row
 * @returns {number} items_completed - items_rejected (clamped ≥ 0)
 */
export function creditedItems(batch) {
  if (!batch) return 0;
  const completed = toNonNegInt(batch.items_completed);
  const rejected = toNonNegInt(batch.items_rejected);
  return Math.max(0, completed - rejected);
}

/**
 * Billable items — accrual revenue count.
 * @param {Object} batch — an annotation_batch row
 * @returns {number} 0 unless Delivered (completed-rejected) or Accepted (items_accepted)
 */
export function billableItems(batch) {
  if (!batch) return 0;
  const status = batch.status;

  if (status === BATCH_STATES.ACCEPTED) {
    // Prefer items_accepted when available. Fall back to tendered count if null
    // (older batches may not have items_accepted populated).
    const acc = batch.items_accepted;
    if (acc != null && Number.isFinite(Number(acc))) {
      return toNonNegInt(acc);
    }
    // Fallback: the accrual basis still applies — items tendered minus rejected.
    return creditedItems(batch);
  }

  if (status === BATCH_STATES.DELIVERED) {
    return creditedItems(batch); // items_completed - items_rejected
  }

  return 0;
}

/**
 * Accepted items — cash-conservative revenue count.
 * @param {Object} batch — an annotation_batch row
 * @returns {number} items_accepted iff status === Accepted AND items_accepted is a number; else 0
 */
export function acceptedItems(batch) {
  if (!batch) return 0;
  if (batch.status !== BATCH_STATES.ACCEPTED) return 0;
  const acc = batch.items_accepted;
  // Strict: if Accepted but items_accepted is null, return 0 (can't confirm).
  if (acc == null || !Number.isFinite(Number(acc))) return 0;
  return toNonNegInt(acc);
}

/**
 * Rejected items — always available for transparency / tooltips / audit.
 * @param {Object} batch
 * @returns {number} items_rejected (or 0)
 */
export function rejectedItems(batch) {
  return intField(batch, 'items_rejected');
}

/**
 * Full breakdown for a single batch. Useful for hover tooltips + debugging.
 * @param {Object} batch
 * @returns {{total, credited, billable, accepted, rejected, remaining, status}}
 */
export function batchBreakdown(batch) {
  if (!batch) {
    return { total: 0, credited: 0, billable: 0, accepted: 0, rejected: 0, remaining: 0, status: null };
  }
  const total = intField(batch, 'total_items');
  const credited = creditedItems(batch);
  const rejected = rejectedItems(batch);
  return {
    total,
    credited,
    billable: billableItems(batch),
    accepted: acceptedItems(batch),
    rejected,
    remaining: Math.max(0, total - credited - rejected),
    status: batch.status ?? null,
  };
}

// ─── Aggregation helpers ─────────────────────────────────────────────────

function sumBy(batches, fn) {
  if (!Array.isArray(batches)) return 0;
  let s = 0;
  for (const b of batches) {
    if (!b) continue;
    s += fn(b);
  }
  return s;
}

/** Sum of credited items across a batch array (skips null/undefined entries). */
export function sumCreditedItems(batches) {
  return sumBy(batches, creditedItems);
}

/** Sum of billable items across a batch array (only Delivered + Accepted contribute). */
export function sumBillableItems(batches) {
  return sumBy(batches, billableItems);
}

/** Sum of accepted items across a batch array (only Accepted contributes). */
export function sumAcceptedItems(batches) {
  return sumBy(batches, acceptedItems);
}

/** Sum of rejected items across a batch array. */
export function sumRejectedItems(batches) {
  return sumBy(batches, rejectedItems);
}

/** Sum of total_items across a batch array. */
export function sumTotalItems(batches) {
  return sumBy(batches, b => intField(b, 'total_items'));
}

/**
 * Per-job aggregate breakdown. One call returns all the counts dashboards need.
 * @param {Array} batches
 * @returns {{total, credited, billable, accepted, rejected, batchCount}}
 */
export function jobBreakdown(batches) {
  if (!Array.isArray(batches) || batches.length === 0) {
    return { total: 0, credited: 0, billable: 0, accepted: 0, rejected: 0, batchCount: 0 };
  }
  return {
    total: sumTotalItems(batches),
    credited: sumCreditedItems(batches),
    billable: sumBillableItems(batches),
    accepted: sumAcceptedItems(batches),
    rejected: sumRejectedItems(batches),
    batchCount: batches.filter(Boolean).length,
  };
}

// ─── Overlap / IAA helper ────────────────────────────────────────────────

/**
 * Filter out overlap (IAA) batches. Most callers want only "main" batches
 * when computing progress/revenue — overlap batches are for quality validation,
 * not deliverables.
 *
 * Callers that want to INCLUDE overlap (e.g., "total annotator work") should
 * skip this filter.
 *
 * @param {Array} batches
 * @returns {Array} batches with is_overlap !== true
 */
export function filterMainBatches(batches) {
  if (!Array.isArray(batches)) return [];
  return batches.filter(b => b && b.is_overlap !== true);
}
